import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Stack,
  Typography,
  keyframes,
} from "@mui/material";

import applicationApi from "@domains/application/module/api/applicationApi";
import { useBuildLogs } from "src/domains/application/hooks/useBuildLogs";
import type { PipelineStep as PipelineStateStep } from "src/domains/application/hooks/useDeploymentStream";
import { useDeploymentStream } from "src/domains/application/hooks/useDeploymentStream";
import { usePipelineDag } from "src/domains/application/hooks/usePipelineDag";
import type { DagNode as DagNodeType } from "src/domains/application/hooks/usePipelineDag";
import {
  getIntentLabel,
  usePipelineRunsApi,
} from "src/domains/application/hooks/usePipelineRunsApi";
import { usePipelineRunSteps } from "src/domains/application/hooks/usePipelineRunSteps";

import {
  type DagNodeData,
  PIPELINE_MODE_LABELS,
  STATUS_COLORS,
} from "./dag-types";
import { LogViewer } from "./LogViewer";
import { type PipelineTabProps } from "./pipeline-tab-helpers";
import { PipelineDAG } from "./PipelineDAG";
import { useEnvironments, useStacksForProject } from "./pipelineDataHooks";
import { PipelineRunsList } from "./PipelineRunsList";
import {
  formatRelativeTime,
  formatDuration,
  shortSha,
} from "./shared/pipeline-helpers";
import { RushaChip } from "./shared/RushaChip";
import { usePipelineSearchParams } from "./usePipelineSearchParams";

// ── Constants ─────────────────────────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ── Run header status icon ────────────────────────────────────────────────────

function RunStatusIcon({ status }: { status: string }): React.ReactElement {
  const map: Record<string, { color: string; icon: React.ReactElement }> = {
    completed: {
      color: STATUS_COLORS.success,
      icon: <CheckIcon sx={{ fontSize: 14, color: "#fff" }} />,
    },
    failed: {
      color: STATUS_COLORS.failed,
      icon: <CloseIcon sx={{ fontSize: 14, color: "#fff" }} />,
    },
    in_progress: {
      color: STATUS_COLORS.running,
      icon: <SyncIcon sx={{ fontSize: 20, color: "#fff" }} />,
    },
    queued: {
      color: STATUS_COLORS.pending,
      icon: <CircularProgress size={16} sx={{ color: "#fff" }} />,
    },
  };
  const cfg = map[status] ?? {
    color: STATUS_COLORS.pending,
    icon: <CircularProgress size={16} sx={{ color: "#fff" }} />,
  };
  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        bgcolor: cfg.color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        animation:
          status === "in_progress" ? `${spin} 1.5s linear infinite` : undefined,
      }}
    >
      {cfg.icon}
    </Box>
  );
}

// ── Auto-select helper ────────────────────────────────────────────────────────

/**
 * Auto-select the first failed job, else first running, else first job of
 * the first phase. Returns the node id to select, or null if no nodes.
 */
function autoSelectNodeId(nodes: DagNodeType[]): string | null {
  if (nodes.length === 0) return null;
  const failed = nodes.find((n) => n.data.status === "failed");
  if (failed) return failed.id;
  const running = nodes.find((n) => n.data.status === "running");
  if (running) return running.id;
  return nodes[0].id;
}

// ── Main Component ────────────────────────────────────────────────────────────

function PipelineTabInner({
  alias,
  templateSlug,
  pipelineMode,
  orgAlias,
  projectAlias,
  targetEnvironmentSlug,
}: PipelineTabProps): React.ReactElement {
  const {
    events,
    loading,
    error,
    refetch,
    pipelineState,
    logBatches,
    pipelineRunRefreshSignal,
  } = useDeploymentStream(alias);

  const { environments, loading: _envLoading } = useEnvironments(
    orgAlias ?? "",
    projectAlias ?? "",
  );
  const { stacks: projectStacks, loading: _stacksLoading } =
    useStacksForProject(orgAlias ?? "", projectAlias ?? "");

  const {
    run: selectedRunId,
    job: selectedJobId,
    phase: _selectedPhase,
    env: selectedEnvSlug,
    setRun: setSelectedRunId,
    setJob: setSelectedJobId,
    setEnv: setSelectedEnvSlug,
  } = usePipelineSearchParams();

  // Default env selection
  useEffect(() => {
    if (environments.length === 0 || selectedEnvSlug) return;
    const matchingEnv = environments.find((e) =>
      projectStacks.some((s) => s.environmentId === e.id && s.alias === alias),
    );
    setSelectedEnvSlug(matchingEnv?.slug ?? environments[0]?.slug ?? null);
  }, [environments, projectStacks, alias, selectedEnvSlug, setSelectedEnvSlug]);

  // API-driven pipeline runs (REST).
  const {
    runs: apiRuns,
    total: apiRunsTotal,
    limit: apiRunsLimit,
    offset: apiRunsOffset,
    hasNext: apiRunsHasNext,
    hasPrevious: apiRunsHasPrevious,
    loading: apiRunsLoading,
    error: apiRunsError,
    setLimit: setApiRunsLimit,
    nextPage: nextApiRunsPage,
    previousPage: previousApiRunsPage,
    refetch: refetchApiRuns,
  } = usePipelineRunsApi(alias, selectedEnvSlug);

  // Refetch API runs when a pipeline_run WS event arrives.
  useEffect(() => {
    if (pipelineRunRefreshSignal > 0) void refetchApiRuns();
  }, [pipelineRunRefreshSignal, refetchApiRuns]);

  // Default-select the newest run on first load so the page opens with a
  // populated DAG instead of "Select a run to view its pipeline".
  useEffect(() => {
    if (apiRuns.length > 0 && !selectedRunId) {
      setSelectedRunId(apiRuns[0].sagaId);
    }
  }, [apiRuns, selectedRunId, setSelectedRunId]);

  // Listen for eager run creation from "Run pipeline" button (useStackActions).
  // Immediately selects the new run and refetches the runs list so the user
  // sees the new "queued" run without a page reload.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.jobId && detail?.systemAlias === alias) {
        setSelectedRunId(detail.jobId);
        setSelectedJobId(null);
        autoSelectRef.current = null;
        void refetchApiRuns();
      }
    };
    window.addEventListener("rusha:pipeline-run-created", handler);
    return () =>
      window.removeEventListener("rusha:pipeline-run-created", handler);
  }, [alias, setSelectedRunId, setSelectedJobId, refetchApiRuns]);

  // ── Template services (for node type badges) ───────────────────────────────
  const [templateServices, setTemplateServices] = useState<
    { name: string; type: string }[] | undefined
  >(undefined);

  // Fetch template + manifest services for DAG structure + type badges.
  // The manifest is always fetched (it's the source of truth for the stack's
  // service list). The template is fetched additionally when a templateSlug
  // exists, to get service type info that may not be in the manifest.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const axios = (await import("axios")).default;

        // Always fetch the manifest — it defines the stack's services.
        const manifestPromise = axios.get(
          `/api/v1/applications/${alias}/manifest`,
          {
            withCredentials: true,
          },
        );

        // Optionally fetch the template for service type info.
        const templatePromise = templateSlug
          ? axios.get(`/api/v1/templates/slug/${templateSlug}`, {
              withCredentials: true,
            })
          : Promise.resolve(null);

        const [tmplRes, manifestRes] = await Promise.all([
          templatePromise,
          manifestPromise,
        ]);
        if (cancelled) return;

        const tmplSvcs = (tmplRes?.data?.services ?? []).map(
          (s: { name: string; type: string }) => ({
            name: s.name,
            type: s.type,
          }),
        );
        const manifestSvcs = (manifestRes.data?.spec?.services ?? []).map(
          (s: { name: string; type: string }) => ({
            name: s.name,
            type: s.type,
          }),
        );
        // Merge, dedupe by name. Manifest takes precedence.
        const map = new Map<string, { name: string; type: string }>();
        for (const s of [...tmplSvcs, ...manifestSvcs]) map.set(s.name, s);
        setTemplateServices([...map.values()]);
      } catch {
        // best-effort
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [templateSlug, alias]);

  // ── Fetch steps scoped to the selected run (REST, per-sagaId) ────────────
  const { steps: runSteps, loading: runStepsLoading } = usePipelineRunSteps(
    alias,
    selectedRunId,
  );

  // Merge REST steps with SSE-updated pipelineState rows for the same saga.
  // REST is the authoritative source for the selected run; SSE rows override
  // with fresher timestamps for real-time updates. This eliminates the
  // silent latest-run fallback that previously showed wrong-run data.
  const selectedRunSteps = useMemo<PipelineStateStep[]>(() => {
    if (!selectedRunId) return [];
    const rest = runSteps ?? [];
    const sse = pipelineState.filter((s) => s.sagaId === selectedRunId);

    // Merge: start with REST, override with SSE if newer
    const map = new Map<string, PipelineStateStep>();
    for (const r of rest) map.set(`${r.step}:${r.serviceName}`, r);
    for (const s of sse) {
      const key = `${s.step}:${s.serviceName}`;
      const existing = map.get(key);
      if (!existing || new Date(s.updatedAt) > new Date(existing.updatedAt)) {
        map.set(key, {
          ...s,
          executions: existing?.executions ?? [],
        });
      }
    }
    return [...map.values()];
  }, [runSteps, pipelineState, selectedRunId]);

  // ── DAG transform ──────────────────────────────────────────────────────────
  // ── Selected run metadata (from apiRuns) ───────────────────────────────────
  const selectedRun = useMemo(
    () => apiRuns.find((r) => r.sagaId === selectedRunId) ?? null,
    [apiRuns, selectedRunId],
  );

  const {
    nodes,
    edges,
    phases,
    loading: dagLoading,
  } = usePipelineDag(
    selectedRunSteps,
    templateServices,
    pipelineMode,
    selectedRun?.intent,
  );

  // ── Auto-selection: when a run is selected and no job is selected via URL,
  // pick first failed → first running → first job. ────────────────────────────
  const autoSelectRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedRunId || nodes.length === 0) return;
    // If URL already has a job selection that exists in the current nodes, keep it.
    if (selectedJobId && nodes.some((n) => n.id === selectedJobId)) {
      autoSelectRef.current = selectedJobId;
      return;
    }
    const auto = autoSelectNodeId(nodes);
    if (auto && autoSelectRef.current !== auto) {
      autoSelectRef.current = auto;
      setSelectedJobId(auto);
    }
  }, [selectedRunId, nodes, selectedJobId, setSelectedJobId]);

  // ── Selected node data ──────────────────────────────────────────────────────
  const selectedNode = useMemo<DagNodeType | null>(() => {
    if (!selectedJobId) return null;
    return nodes.find((n) => n.id === selectedJobId) ?? null;
  }, [nodes, selectedJobId]);

  const selectedNodeData = selectedNode?.data ?? null;
  const [selectedExecutionId, setSelectedExecutionId] = useState<number | null>(
    null,
  );
  useEffect(() => {
    setSelectedExecutionId(null);
  }, [selectedRunId]);

  // ── Logs for the selected job ───────────────────────────────────────────────
  const effectiveServiceName = selectedNodeData?.service || undefined;
  const isRushaOwnedBuild = pipelineMode === "rusha_managed";

  const scopedLogBatches = useMemo(() => {
    const sagaId = selectedRunId;
    if (!sagaId) return new Map<string, Map<string, string[]>>();
    return logBatches.get(sagaId) ?? new Map<string, Map<string, string[]>>();
  }, [logBatches, selectedRunId]);

  const serviceLogs = useBuildLogs(alias, effectiveServiceName, {
    autoFetch: false,
    pollWhileActive:
      (selectedNodeData?.phase === "build" ||
        selectedNodeData?.phase === "deploy") &&
      selectedNodeData?.status === "running",
    logBatches: scopedLogBatches,
    buildMode: isRushaOwnedBuild ? "rusha_managed" : "ci",
    sagaId: selectedRunId ?? undefined,
  });

  // ── Step events for the selected job (for deriveSteps fallback) ─────────────
  const stepEvents = useMemo(() => {
    if (!selectedNodeData) return [];
    const stepKey =
      selectedNodeData.phase === "deploy" || selectedNodeData.phase === "verify"
        ? "deploy"
        : selectedNodeData.phase === "security_scan"
          ? "security_scan"
          : selectedNodeData.phase;
    return events.filter(
      (e) =>
        e.sagaId === selectedRunId &&
        e.step === stepKey &&
        (e.metadata?.serviceName === selectedNodeData.service ||
          (!selectedNodeData.service && !e.metadata?.serviceName)),
    );
  }, [events, selectedRunId, selectedNodeData]);

  // ── Node click handler ──────────────────────────────────────────────────────
  const handleNodeClick = useCallback(
    (nodeId: string, _data: DagNodeData) => {
      autoSelectRef.current = nodeId;
      setSelectedExecutionId(null);
      setSelectedJobId(nodeId);
    },
    [setSelectedJobId],
  );

  const handleExecutionClick = useCallback(
    (nodeId: string, executionId: number) => {
      autoSelectRef.current = nodeId;
      setSelectedJobId(nodeId);
      setSelectedExecutionId(executionId);
    },
    [setSelectedJobId],
  );

  // ── Pipeline status ─────────────────────────────────────────────────────────
  const buildFailed = selectedRun?.status === "failed";

  // ── Retry / redeploy actions ────────────────────────────────────────────────
  const [retryLoading, setRetryLoading] = useState(false);
  const [redeployLoading, setRedeployLoading] = useState(false);

  const handleRetryBuild = useCallback(async () => {
    if (!selectedRunId) return;
    setRetryLoading(true);
    try {
      const result = await applicationApi.retryRun(
        alias,
        selectedRunId,
        selectedRun?.intent === "deploy" ? "deploy" : "release",
      );
      if (result.jobId) {
        setSelectedRunId(result.jobId);
        setSelectedJobId(null);
        autoSelectRef.current = null;
      }
      setTimeout(() => {
        void refetchApiRuns();
        void refetch();
      }, 1500);
    } finally {
      setRetryLoading(false);
    }
  }, [
    alias,
    refetch,
    refetchApiRuns,
    selectedRun,
    selectedRunId,
    setSelectedRunId,
    setSelectedJobId,
  ]);

  const handleRedeploy = useCallback(async () => {
    if (!targetEnvironmentSlug) return;
    setRedeployLoading(true);
    try {
      const result = await applicationApi.buildAndDeploy(
        alias,
        targetEnvironmentSlug,
      );
      if (result.jobId) {
        setSelectedRunId(result.jobId);
        setSelectedJobId(null);
        autoSelectRef.current = null;
      }
      setTimeout(() => {
        void refetchApiRuns();
        void refetch();
      }, 1500);
    } finally {
      setRedeployLoading(false);
    }
  }, [
    alias,
    refetch,
    refetchApiRuns,
    setSelectedRunId,
    setSelectedJobId,
    targetEnvironmentSlug,
  ]);

  // ── Run annotation (for failed runs) ────────────────────────────────────────
  const runAnnotation = useMemo(() => {
    if (!selectedRun || selectedRun.status !== "failed") return null;

    // Prefer the enriched failureMessage from the API (derived from the
    // first failed PipelineStep.message by the run enrichment query).
    if (selectedRun.failureMessage) {
      const phasePart = selectedRun.failedAt
        ? `${selectedRun.failedAt} failed: `
        : "";
      return `${phasePart}${selectedRun.failureMessage}`;
    }

    // Fallback: derive from loaded step rows.
    const failedStep = selectedRunSteps.find((s) => s.status === "failed");
    if (!failedStep) {
      return "Pipeline run failed.";
    }
    const phaseLabel =
      failedStep.step.charAt(0).toUpperCase() + failedStep.step.slice(1);
    const svcPart = failedStep.serviceName
      ? ` for ${failedStep.serviceName}`
      : "";
    return `${phaseLabel} failed${svcPart}: ${failedStep.message}`;
  }, [selectedRun, selectedRunSteps]);

  // ── Render ──────────────────────────────────────────────────────────────────

  // Error state for SSE connection issues
  if (error && !loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          py: 1.5,
          px: 2,
          mb: 2.5,
          borderRadius: "8px",
          bgcolor: "rgba(244,63,94,0.12)",
          border: "1px solid rgba(244,63,94,0.3)",
          color: "error.main",
          fontSize: 13,
        }}
      >
        <span>{error}</span>
        <Button
          size="small"
          onClick={() => void refetch()}
          sx={{ textTransform: "none" }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "320px minmax(0, 1fr)",
          gap: 2,
          alignItems: "start",
        }}
      >
        {/* Left rail: run list */}
        <Box sx={{ flexShrink: 0 }}>
          <PipelineRunsList
            pipelineMode={pipelineMode ?? ""}
            runs={apiRuns}
            total={apiRunsTotal}
            limit={apiRunsLimit}
            offset={apiRunsOffset}
            hasNext={apiRunsHasNext}
            hasPrevious={apiRunsHasPrevious}
            loading={apiRunsLoading}
            error={apiRunsError}
            onLimitChange={setApiRunsLimit}
            onNextPage={nextApiRunsPage}
            onPreviousPage={previousApiRunsPage}
            onRetry={() => void refetchApiRuns()}
            selectedRunId={selectedRunId}
            onSelectRun={setSelectedRunId}
          />
        </Box>

        {/* Right column: run header + DAG + log viewer */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {selectedRun && runStepsLoading && selectedRunSteps.length === 0 ? (
            /* Loading state: run is selected but steps are still fetching */
            <Stack spacing={2}>
              <Box
                sx={{
                  border: (t) => `1px solid ${t.palette.divider}`,
                  borderRadius: "12px",
                  bgcolor: (t) => t.palette.background.paper,
                  p: "18px 20px",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Skeleton variant="circular" width={26} height={26} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" height={20} />
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={16}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Stack>
              </Box>
              <PipelineDAG
                nodes={[]}
                edges={[]}
                phases={[]}
                selectedNodeId={null}
                onNodeClick={() => {}}
                loading
              />
            </Stack>
          ) : selectedRun ? (
            <Stack spacing={2}>
              {/* Run header */}
              <Box
                sx={{
                  border: (t) => `1px solid ${t.palette.divider}`,
                  borderRadius: "12px",
                  bgcolor: (t) => t.palette.background.paper,
                  p: "18px 20px",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    minWidth={0}
                  >
                    <RunStatusIcon status={selectedRun.status} />
                    <Box minWidth={0}>
                      <Typography sx={{ fontSize: 15, fontWeight: 700 }} noWrap>
                        {selectedRun.commitMessage ??
                          getIntentLabel(selectedRun.intent)}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}
                      >
                        {selectedRun.branch && (
                          <RushaChip
                            label={selectedRun.branch}
                            size="sm"
                            mono
                          />
                        )}
                        {shortSha(selectedRun.sha) && (
                          <RushaChip
                            label={shortSha(selectedRun.sha)}
                            size="sm"
                            mono
                          />
                        )}
                        <RushaChip
                          label={
                            PIPELINE_MODE_LABELS[pipelineMode ?? ""] ??
                            pipelineMode ??
                            ""
                          }
                          size="sm"
                        />
                        {selectedRun.environment && (
                          <RushaChip
                            label={`target: ${selectedRun.environment}`}
                            size="sm"
                          />
                        )}
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        >
                          {getIntentLabel(selectedRun.intent)} ·{" "}
                          {formatRelativeTime(selectedRun.createdAt)}
                          {selectedRun.completedAt &&
                            ` · ran for ${formatDuration(
                              Math.round(
                                (new Date(selectedRun.completedAt).getTime() -
                                  new Date(selectedRun.createdAt).getTime()) /
                                  1000,
                              ),
                            )}`}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Re-run actions */}
                  <Stack direction="row" spacing={1} flexShrink={0}>
                    {buildFailed && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ReplayIcon />}
                        onClick={handleRetryBuild}
                        disabled={retryLoading}
                        sx={{ textTransform: "none", borderRadius: 1.5 }}
                      >
                        Retry run
                      </Button>
                    )}
                    {isRushaOwnedBuild && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        onClick={handleRedeploy}
                        disabled={redeployLoading}
                        sx={{ textTransform: "none", borderRadius: 1.5 }}
                      >
                        Re-run all
                      </Button>
                    )}
                  </Stack>
                </Stack>

                {/* Annotation banner for failed runs */}
                {runAnnotation && (
                  <Alert
                    severity="error"
                    icon={false}
                    sx={{
                      mt: 1.75,
                      fontSize: "12.5px",
                      border: "1px solid rgba(244,63,94,0.4)",
                      bgcolor: "rgba(244,63,94,0.08)",
                      borderRadius: "8px",
                      "& .MuiAlert-message": { py: 0.5 },
                    }}
                  >
                    {runAnnotation}
                  </Alert>
                )}
              </Box>

              {/* DAG */}
              <PipelineDAG
                nodes={nodes}
                edges={edges}
                phases={phases}
                selectedNodeId={selectedJobId}
                onNodeClick={handleNodeClick}
                onExecutionClick={handleExecutionClick}
                loading={dagLoading}
              />

              {/* Log viewer */}
              <LogViewer
                alias={alias}
                sagaId={selectedRunId}
                nodeData={selectedNodeData}
                selectedExecutionId={selectedExecutionId}
                sha={selectedRun.sha}
                liveLogs={serviceLogs.liveLogs}
                buildLogJobs={serviceLogs.logs}
                qualityGates={serviceLogs.qualityGates}
                stepEvents={stepEvents}
                runHtmlUrl={serviceLogs.runHtmlUrl}
              />
            </Stack>
          ) : (
            <Box
              sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: "12px",
                bgcolor: (t) => t.palette.background.paper,
                p: 6,
                textAlign: "center",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                Select a run to view its pipeline
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a run from the left to see its build, scan, deploy, and
                verify stages.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Stack>
  );
}

export function PipelineTab(props: PipelineTabProps): React.ReactElement {
  return <PipelineTabInner {...props} />;
}

export default PipelineTab;
